/**
 * JSON Service for formatting, compression, and escaping operations
 */
import unescapeJs from 'unescape-js';

class JSONService {
  /**
   * Format (prettify) JSON content
   * @param {string} content - Raw JSON string
   * @returns {Object} - {success: boolean, data?: string, error?: string}
   */
  format(content) {
    if (!content || !content.trim()) {
      return {
        success: false,
        error: 'Content is empty'
      };
    }

    try {
      // Parse JSON to validate syntax
      const parsed = JSON.parse(content.trim());
      
      // Format with 2-space indentation
      const formatted = JSON.stringify(parsed, null, 2);
      
      return {
        success: true,
        data: formatted
      };
    } catch (error) {
      // Extract error details for user-friendly message
      const errorMessage = this.parseJSONError(error.message, content);
      
      return {
        success: false,
        error: `Invalid JSON: ${errorMessage}`
      };
    }
  }

  /**
   * Compress (minify) JSON content
   * @param {string} content - JSON string to compress
   * @returns {Object} - {success: boolean, data?: string, error?: string}
   */
  compress(content) {
    if (!content || !content.trim()) {
      return {
        success: false,
        error: 'Content is empty'
      };
    }

    try {
      // Parse JSON to validate syntax
      const parsed = JSON.parse(content.trim());
      
      // Stringify without spacing
      const compressed = JSON.stringify(parsed);
      
      return {
        success: true,
        data: compressed
      };
    } catch (error) {
      const errorMessage = this.parseJSONError(error.message, content);
      
      return {
        success: false,
        error: `Invalid JSON: ${errorMessage}`
      };
    }
  }

  /**
   * Escape JSON for embedding in strings using native JSON.stringify
   * @param {string} content - JSON content to escape
   * @returns {Object} - {success: boolean, data?: string, error?: string}
   */
  escape(content) {
    if (!content) {
      return {
        success: false,
        error: 'Content is empty'
      };
    }

    try {
      // Use JSON.stringify to properly escape the string, then remove outer quotes
      // This ensures proper handling of all JSON escape sequences
      const jsonEscaped = JSON.stringify(content);
      // Remove the outer quotes added by JSON.stringify
      const escaped = jsonEscaped.slice(1, -1);

      return {
        success: true,
        data: escaped
      };
    } catch (error) {
      return {
        success: false,
        error: `Escape failed: ${error.message}`
      };
    }
  }

  /**
   * Unescape JSON from string format using unescape-js library
   * @param {string} content - Escaped JSON string
   * @returns {Object} - {success: boolean, data?: string, error?: string}
   */
  unescape(content) {
    if (!content) {
      return {
        success: false,
        error: 'Content is empty'
      };
    }

    try {
      // Method 1: Try using unescape-js library for robust unescaping
      let unescaped = unescapeJs(content);
      
      // Method 2: If the result looks like JSON, try to format it nicely
    //   if (typeof unescaped === 'string' && 
    //       (unescaped.trim().startsWith('{') || unescaped.trim().startsWith('['))) {
    //     try {
    //       const parsed = JSON.parse(unescaped);
    //       unescaped = JSON.stringify(parsed, null, 2);
    //     } catch (parseError) {
    //       // If it can't be parsed as JSON, just return the unescaped string
    //       // This is fine - the content might not be JSON
    //     }
    //   }

      return {
        success: true,
        data: unescaped
      };
    } catch (error) {
      // Fallback: If unescape-js fails, try simple JSON.parse approach
      try {
        const fallbackUnescaped = JSON.parse('"' + content + '"');
        return {
          success: true,
          data: fallbackUnescaped
        };
      } catch (fallbackError) {
        return {
          success: false,
          error: `Unescape failed: ${error.message}`
        };
      }
    }
  }

  /**
   * Validate if content is valid JSON
   * @param {string} content - Content to validate
   * @returns {boolean} - True if valid JSON
   */
  isValidJSON(content) {
    if (!content || !content.trim()) {
      return false;
    }

    try {
      JSON.parse(content.trim());
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Detect if content appears to be escaped JSON
   * @param {string} content - Content to check
   * @returns {boolean} - True if appears to be escaped
   */
  isEscapedJSON(content) {
    if (!content) return false;
    
    // Check for common escape patterns
    const hasEscapedQuotes = content.includes('\\"');
    const hasEscapedNewlines = content.includes('\\n');
    const hasEscapedBackslashes = content.includes('\\\\');
    
    return hasEscapedQuotes || hasEscapedNewlines || hasEscapedBackslashes;
  }

  /**
   * Detect if content appears to be formatted (pretty) JSON
   * @param {string} content - Content to check
   * @returns {boolean} - True if appears to be formatted
   */
  isFormattedJSON(content) {
    if (!content || !this.isValidJSON(content)) return false;
    
    // Check for indentation and line breaks
    const hasIndentation = /\n\s+/.test(content);
    const hasLineBreaks = content.includes('\n');
    
    return hasIndentation && hasLineBreaks;
  }

  /**
   * Detect if content appears to be compressed (minified) JSON
   * @param {string} content - Content to check
   * @returns {boolean} - True if appears to be compressed
   */
  isCompressedJSON(content) {
    if (!content || !this.isValidJSON(content)) return false;
    
    // Check for lack of unnecessary whitespace
    const hasMinimalWhitespace = !content.includes('\n') && !/\s{2,}/.test(content);
    
    return hasMinimalWhitespace;
  }

  /**
   * Parse JSON error message to provide user-friendly feedback
   * @param {string} errorMessage - Original error message
   * @param {string} content - Original content that failed
   * @returns {string} - User-friendly error message
   */
  parseJSONError(errorMessage, content) {
    // Extract position information if available
    const positionMatch = errorMessage.match(/position (\d+)/);
    const lineMatch = errorMessage.match(/line (\d+)/);
    
    if (positionMatch) {
      const position = parseInt(positionMatch[1]);
      const beforeError = content.substring(0, position);
      const lineNumber = beforeError.split('\n').length;
      const columnNumber = beforeError.split('\n').pop().length + 1;
      
      return `Syntax error at line ${lineNumber}, column ${columnNumber}`;
    } else if (lineMatch) {
      return `Syntax error at line ${lineMatch[1]}`;
    } else if (errorMessage.includes('Unexpected token')) {
      const tokenMatch = errorMessage.match(/Unexpected token (.+?) in JSON/);
      if (tokenMatch) {
        return `Unexpected character '${tokenMatch[1]}'`;
      }
      return 'Unexpected character in JSON';
    } else if (errorMessage.includes('Unexpected end')) {
      return 'Incomplete JSON - missing closing bracket or quote';
    } else {
      return errorMessage;
    }
  }
}

// Export singleton instance
export const jsonService = new JSONService();

// Export class for creating new instances if needed
export { JSONService };
